import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import isBetween from 'dayjs/plugin/isBetween';
import localeData from 'dayjs/plugin/localeData';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import 'dayjs/locale/fr';

// Register ALL plugins MUI X / the app can rely on, centrally and BEFORE any
// picker/adapter is used. AdapterDayjs checks hasUTCPlugin() etc at runtime.
//
// NOTE: the dayjs `timezone` plugin is deliberately NOT registered. react-big-calendar's dayjs
// localizer switches to a timezone-aware path that performs an Intl.DateTimeFormat offset
// computation for every date operation once the shared dayjs instance has `.tz`, making month
// views with hundreds/thousands of events take many seconds (app freeze). The MUI X dayjs
// adapter only needs the `utc` plugin (not timezone).
dayjs.extend(utc);
dayjs.extend(customParseFormat);
dayjs.extend(localizedFormat);
dayjs.extend(advancedFormat);
dayjs.extend(weekOfYear);
dayjs.extend(isBetween);
dayjs.extend(localeData);
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

export const pickerDayjs = dayjs;

/**
 * Normalizes any picker value (dayjs, epoch ms number, ISO string, Date) into a
 * valid LOCAL dayjs — or null when invalid/empty. UTC-forced instances
 * (moment-compat $u) are converted with .local() so adapters never mix modes.
 */
export const toLocalDayjsOrNull = (value: any): any => {
    if (value == null || value === '') {
        return null;
    }
    let parsed: any;
    if (dayjs.isDayjs(value)) {
        parsed = value;
    } else if (typeof value === 'number' || typeof value === 'string' || value instanceof Date) {
        parsed = dayjs(value);
    } else {
        return null;
    }
    if (!parsed || typeof parsed.isValid !== 'function' || !parsed.isValid()) {
        return null;
    }
    if (parsed.$u) {
        try {
            parsed = parsed.local();
        } catch (e) {
            /* keep original if .local() unavailable */
        }
    }
    return parsed;
};

/**
 * Converts any activeLanguage shape (string 'fr-FR', array ['fr','FR']) into a
 * valid dayjs locale name ('fr'). Guards against callers doing `string[0]`
 * which would produce an unknown one-char locale like 'f'.
 */
export const dayjsLocaleOf = (value: any): string => {
    const raw = Array.isArray(value) ? value[0] : value;
    const locale = String(raw ?? 'en').trim().split('-')[0].split('_')[0];
    return locale || 'en';
};

/**
 * Stable memo key for a picker value. Two different dayjs objects representing
 * the same instant share the same key — so inline `dayjs(x)` values no longer
 * cause a new normalized object (and picker re-render loops) on every render.
 */
export const pickerValueKey = (value: any): string => {
    if (value == null || value === '') {
        return '';
    }
    if (dayjs.isDayjs(value)) {
        return `t${value.valueOf()}`;
    }
    if (typeof value === 'number') {
        return `n${value}`;
    }
    if (value instanceof Date) {
        return `d${value.getTime()}`;
    }
    if (typeof value === 'string') {
        return `s${value}`;
    }
    return String(value);
};
